import { useState, useMemo } from "react";
import { Button } from "components/ui/button";
import { Label } from "components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "components/ui/command";
import { Check, CaretUpDown } from "@phosphor-icons/react";
import { CreateMaintenanceScheduleDto } from "shared/types/asset";
import {
  useAssets,
  useCreateMaintenanceSchedule,
} from "hooks/useAssets";
import { toast } from "react-toastify";
import {
  MaintenanceFormFields,
  MaintenanceFormData,
  EMPTY_MAINTENANCE_FORM,
} from "./MaintenanceEditDialog";

interface MaintenanceCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
  /** When provided, the asset selector is skipped */
  assetId?: string;
}

export function MaintenanceCreateDialog({
  open,
  onOpenChange,
  onCreated,
  assetId: fixedAssetId,
}: MaintenanceCreateDialogProps) {
  const { assets, isLoading: assetsLoading } = useAssets();
  const { createSchedule, isCreating } = useCreateMaintenanceSchedule();

  const [selectedAssetId, setSelectedAssetId] = useState<string>("");
  const [assetPopoverOpen, setAssetPopoverOpen] = useState(false);
  const [formData, setFormData] = useState<MaintenanceFormData>(
    EMPTY_MAINTENANCE_FORM
  );

  const effectiveAssetId = fixedAssetId || selectedAssetId;

  const selectedAsset = useMemo(
    () => (fixedAssetId ? null : assets.find((a) => a.id === selectedAssetId)),
    [assets, selectedAssetId, fixedAssetId]
  );

  const resetForm = () => {
    setSelectedAssetId("");
    setFormData(EMPTY_MAINTENANCE_FORM);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetForm();
    }
    onOpenChange(nextOpen);
  };

  const handleSave = async () => {
    if (!effectiveAssetId) {
      toast.error("Selecteer een voorziening");
      return;
    }

    if (!formData.name) {
      toast.error("Naam is verplicht");
      return;
    }

    if (formData.dateMode === "next" && !formData.nextDueDate) {
      toast.error("Volgende onderhoudsdatum is verplicht");
      return;
    }

    if (formData.dateMode === "last" && !formData.lastCompletedDate) {
      toast.error("Laatste onderhoudsdatum is verplicht");
      return;
    }

    try {
      const dto: CreateMaintenanceScheduleDto = {
        name: formData.name,
        frequency: formData.frequency,
        maintenanceType: formData.maintenanceType,
        customFrequencyMonths: formData.customFrequencyMonths,
      };

      if (formData.dateMode === "last") {
        dto.lastCompletedDate = formData.lastCompletedDate;
      } else {
        dto.firstDueDate = formData.nextDueDate;
      }

      await createSchedule(effectiveAssetId, dto);
      toast.success("Onderhoudsschema aangemaakt");
      handleOpenChange(false);
      onCreated();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Kon onderhoudsschema niet aanmaken";
      toast.error(message);
      console.error("Error creating maintenance schedule:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Onderhoudsschema toevoegen</DialogTitle>
          <DialogDescription>
            {fixedAssetId
              ? "Vul de onderhoudsgegevens in."
              : "Selecteer een voorziening en vul de onderhoudsgegevens in."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          {/* Asset selector — only shown when no fixed assetId */}
          {!fixedAssetId && (
            <div className="space-y-1">
              <Label className="text-xs">Voorziening</Label>
              <Popover
                open={assetPopoverOpen}
                onOpenChange={setAssetPopoverOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={assetPopoverOpen}
                    className="w-full justify-between font-normal"
                  >
                    {selectedAsset ? (
                      <span className="truncate">
                        {selectedAsset.name}
                        {selectedAsset.building?.id && (
                          <span className="text-muted-foreground">
                            {" "}
                            — {selectedAsset.building.id}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">
                        {assetsLoading
                          ? "Voorzieningen laden..."
                          : "Selecteer een voorziening..."}
                      </span>
                    )}
                    <CaretUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                  <Command>
                    <CommandInput placeholder="Zoek op naam of gebouw..." />
                    <CommandList>
                      <CommandEmpty>Geen voorzieningen gevonden.</CommandEmpty>
                      <CommandGroup>
                        {assets.map((asset) => (
                          <CommandItem
                            key={asset.id}
                            value={`${asset.name} ${asset.building?.id ?? ""}`}
                            onSelect={() => {
                              setSelectedAssetId(asset.id);
                              setAssetPopoverOpen(false);
                            }}
                          >
                            <Check
                              className={`mr-2 h-4 w-4 ${
                                selectedAssetId === asset.id
                                  ? "opacity-100"
                                  : "opacity-0"
                              }`}
                            />
                            <div className="flex flex-col">
                              <span>{asset.name}</span>
                              {asset.building?.id && (
                                <span className="text-xs text-muted-foreground">
                                  {asset.building.id}
                                </span>
                              )}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* Form fields */}
          <MaintenanceFormFields
            formData={formData}
            setFormData={setFormData}
          />

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleOpenChange(false)}
            >
              Annuleer
            </Button>
            <Button size="sm" onClick={handleSave} disabled={isCreating}>
              {isCreating ? "Opslaan..." : "Opslaan"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
